import { getIdToken } from "firebase/auth";
import firebase from "./firebase";
import { SendMessageRequest, SendMessageResponse, StreamGiftsResponse, StreamMentionableUsersResponse } from "./models/stream-message";
import PostStatusCache from "./post-status-cache";

interface ServerTimeResponse {
  timestamp: {
    _seconds: number;
    _nanoseconds: number;
  };
  milliseconds: number;
  seconds: number;
  nanoseconds: number;
}

type PaginationArg =
  | string
  | {
      limit?: number;
      next?: string | null;
    };

type DraftMentionPayload = {
  slug: string;
  displayName: string;
  type: "user" | "creator";
  startIndex: number;
  endIndex: number;
  id?: string;
  picture?: string;
};

type DraftMediaPayload = {
  id: string;
  type: "image" | "video";
  source: string;
  aspectRatio?: number;
  coverPhoto?: string; // For videos: user-uploaded cover photo path
};

type DraftPayload = {
  text?: string | null;
  mentions?: DraftMentionPayload[] | null;
  media?: DraftMediaPayload[] | null;
  scheduledAt?: number | null; // Milliseconds timestamp for scheduled publish time
};

// API Client 單例
export class APIClient {
  private serverUrl: string;
  private creatorServerUrl: string;

  constructor(serverUrl?: string, creatorServerUrl?: string) {
    this.serverUrl = serverUrl || import.meta.env.VITE_BACKEND_SERVER;
    this.creatorServerUrl = creatorServerUrl || import.meta.env.VITE_CREATOR_BACKEND_SERVER;
  }

  async apiRequest(
    endpoint: string,
    { optionalAuth = false, ...options }: any = {},
  ) {
    try {
      if (!this.serverUrl) {
        throw new Error("Server URL is not defined");
      }

      const currentUser = firebase.auth?.currentUser;
      if (!optionalAuth && !currentUser) return null;

      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };

      const token = currentUser ? await getIdToken(currentUser) : null;
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      const response = await fetch(`${this.serverUrl}${endpoint}`, {
        ...options,
        headers,
      });

      const acceptedStatuses = [200, 201, 204, 206, 207];
      if (!response.ok) {
        let errorBody: any = null;
        let message = `HTTP error! status: ${response.status}`;
        try {
          errorBody = await response.json();
          if (errorBody?.error) {
            message = errorBody.error;
          }
        } catch {
          try {
            errorBody = await response.text();
            if (errorBody) {
              message = errorBody;
            }
          } catch {
            // ignore
          }
        }
        const error: any = new Error(message);
        error.status = response.status;
        error.body = errorBody;
        throw error;
      }

      if (acceptedStatuses.includes(response.status)) {
        if (response.status === 204) return null;
        return await response.json();
      }
      return await response.text();
    } catch (error) {
      console.error("API Request failed:", error);
      throw error;
    }
  }

  async creatorApiRequest(endpoint: string, { optionalAuth = false, ...options }: any = {}) {
    const currentUser = firebase.auth?.currentUser;
    if (!optionalAuth && !currentUser) return null;

    const token = currentUser ? await getIdToken(currentUser) : null;
    if (!token) {
      throw new Error("No token found");
    }
    const defaultHeaders: Record<string, string> = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    const response = await fetch(`${this.creatorServerUrl}${endpoint}`, {
      ...options,
      headers: {
        ...defaultHeaders,
        ...(options.headers || {}),
      },
    });

    const acceptedStatuses = [200, 201, 206, 207];
    if (!response.ok) {
      let errorBody: any = null;
      let message = `HTTP error! status: ${response.status}`;
      try {
        errorBody = await response.json();
        if (errorBody?.error) {
          message = errorBody.error;
        }
      } catch {
        try {
          errorBody = await response.text();
          if (errorBody) {
            message = errorBody;
          }
        } catch {
          // ignore
        }
      }
      const error: any = new Error(message);
      error.status = response.status;
      error.body = errorBody;
      throw error;
    }
    if (acceptedStatuses.includes(response.status)) {
      return await response.json();
    }
    return await response.text();
  }

  private buildPaginationQuery(params?: PaginationArg) {
    const normalized =
      typeof params === "string" ? { next: params } : params || {};
    const query = new URLSearchParams();
    if (normalized.limit) {
      query.set("limit", normalized.limit.toString());
    }
    if (normalized.next) {
      query.set("next", normalized.next);
    }
    const queryString = query.toString();
    return queryString ? `?${queryString}` : "";
  }

  // ======================
  // Posts (feed/detail/creator)
  // ======================
  async getPostsFeed(params?: PaginationArg) {
    const query = this.buildPaginationQuery(params);
    return this.apiRequest(`/feed/posts${query}`, {optionalAuth: true});
  }

  async getPost(postId: string) {
    const res = await this.apiRequest(`/posts/${postId}`, {optionalAuth: true});
    return res?.post || res;
  }

  async createPostDraft(payload: DraftPayload) {
    return this.creatorApiRequest(`/posts/drafts`, {
      method: "POST",
      body: JSON.stringify(payload || {}),
    });
  }

  async updatePostDraft(draftId: string, payload: DraftPayload) {
    return this.creatorApiRequest(`/posts/drafts/${draftId}`, {
      method: "PUT",
      body: JSON.stringify(payload || {}),
    });
  }

  async deletePostDraft(draftId: string) {
    return this.creatorApiRequest(`/posts/drafts/${draftId}`, {
      method: "DELETE",
    });
  }

  async submitPostDraft(draftId: string) {
    return this.creatorApiRequest(`/posts/drafts/${draftId}/submit`, {
      method: "POST",
      body: JSON.stringify({}),
    });
  }

  async listDrafts(params?: PaginationArg) {
    const query = this.buildPaginationQuery(params);
    return this.creatorApiRequest(`/posts/drafts${query}`, {});
  }

  async getDraft(draftId: string) {
    return this.creatorApiRequest(`/posts/drafts/${draftId}`, {});
  }

  async listMyPosts(params?: PaginationArg) {
    const query = this.buildPaginationQuery(params);
    return this.apiRequest(`/posts${query}`, {});
  }

  async getGuestVideos(start?: string) {
    return this.apiRequest(`/videos/guest?start=${start}`, {
      optionalAuth: true,
    });
  }

  async getVideosFeed(start?: string) {
    return this.apiRequest(`/feed/videos?start=${start}`);
  }

  async getCreator(creatorId: string) {
    const isSlug = creatorId.startsWith("@");
    const cleanId = isSlug ? creatorId.slice(1) : creatorId;
    const endpoint = isSlug
      ? `/creators/slug/${cleanId}`
      : `/creators/${cleanId}`;
    return this.apiRequest(endpoint, { optionalAuth: true });
  }

  async followCreator(creatorId: string) {
    return this.apiRequest(`/followed-creators/${creatorId}`, {
      method: "POST",
    });
  }

  async unfollowCreator(creatorId: string) {
    return this.apiRequest(`/followed-creators/${creatorId}`, {
      method: "DELETE",
    });
  }

  async getExplore() {
    return this.apiRequest(`/explore`, { optionalAuth: true });
  }

  async getGifts(): Promise<
    {
      id: string;
      group: string;
      price: number;
      name?: string;
      path?: string;
      effect?: string[];
      animation?: {
        path: string;
        duration?: number;
        loop?: boolean;
      };
    }[]
  > {
    return this.apiRequest(`/gifts`, { optionalAuth: true });
  }

  async getCreatorVideos(creatorId: string, next?: string | null) {
    return this.apiRequest(`/creators/${creatorId}/videos?next=${next || ""}`, {
      optionalAuth: true,
    });
  }

  async getCreatorPosts(creatorId: string, next?: string) {
    const query = this.buildPaginationQuery(next);
    return this.apiRequest(
      `/creators/${creatorId}/posts${query}`,
      { optionalAuth: true },
    );
  }

  async unpublishPost(postId: string) {
    return this.creatorApiRequest(`/posts/${postId}/unpublish`, {
      method: "POST",
    });
  }

  // ======================
  // Post Likes
  // ======================
  async checkPostLiked(postId: string): Promise<boolean> {
    try {
      const currentUser = firebase.auth?.currentUser;
      if (!currentUser) return false;

      // Check cache first
      const cached = PostStatusCache.getCachedStatus(postId, "liked");
      if (cached !== null) {
        return cached;
      }
      
      const token = await getIdToken(currentUser);
      const response = await fetch(`${this.serverUrl}/liked-posts/${postId}`, {
        method: "HEAD",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const isLiked = response.status === 200;
      
      // Cache the result
      PostStatusCache.setCachedStatus(postId, "liked", isLiked);
      
      return isLiked;
    } catch (error) {
      console.error("Failed to check if post is liked:", error);
      return false;
    }
  }

  async getLikedPosts(params?: PaginationArg) {
    const query = this.buildPaginationQuery(params);
    return this.apiRequest(`/liked-posts${query}`, {});
  }

  async likePost(postId: string) {
    const result = await this.apiRequest(`/liked-posts/${postId}`, {
      method: "POST",
    });
    // Invalidate cache and update with new value
    PostStatusCache.invalidateStatus(postId, "liked");
    PostStatusCache.setCachedStatus(postId, "liked", true);
    return result;
  }

  async unlikePost(postId: string) {
    const result = await this.apiRequest(`/liked-posts/${postId}`, {
      method: "DELETE",
    });
    // Invalidate cache and update with new value
    PostStatusCache.invalidateStatus(postId, "liked");
    PostStatusCache.setCachedStatus(postId, "liked", false);
    return result;
  }

  // ======================
  // Post Saves
  // ======================
  async checkPostSaved(postId: string): Promise<boolean> {
    try {
      const currentUser = firebase.auth?.currentUser;
      if (!currentUser) return false;

      // Check cache first
      const cached = PostStatusCache.getCachedStatus(postId, "saved");
      if (cached !== null) {
        return cached;
      }
      
      const token = await getIdToken(currentUser);
      const response = await fetch(`${this.serverUrl}/saved-posts/${postId}`, {
        method: "HEAD",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const isSaved = response.status === 200;
      
      // Cache the result
      PostStatusCache.setCachedStatus(postId, "saved", isSaved);
      
      return isSaved;
    } catch (error) {
      console.error("Failed to check if post is saved:", error);
      return false;
    }
  }

  async getSavedPosts(params?: PaginationArg) {
    const query = this.buildPaginationQuery(params);
    return this.apiRequest(`/saved-posts${query}`, {});
  }

  async savePost(postId: string) {
    const result = await this.apiRequest(`/saved-posts/${postId}`, {
      method: "POST",
    });
    // Invalidate cache and update with new value
    PostStatusCache.invalidateStatus(postId, "saved");
    PostStatusCache.setCachedStatus(postId, "saved", true);
    return result;
  }

  async unsavePost(postId: string) {
    const result = await this.apiRequest(`/saved-posts/${postId}`, {
      method: "DELETE",
    });
    // Invalidate cache and update with new value
    PostStatusCache.invalidateStatus(postId, "saved");
    PostStatusCache.setCachedStatus(postId, "saved", false);
    return result;
  }

  // api.getExploreSettingsByChunk = useCallback(
  //   async (id, offset = 0, limit = 5) =>
  //     apiRequest(`/explore/chunks/${id}?offset=${offset}&limit=${limit}`),
  //   [apiRequest]
  // );

  async getExploreSettingsByChunk(id: string, offset = 0, limit = 5) {
    return this.apiRequest(
      `/explore/chunks/${id}?offset=${offset}&limit=${limit}`,
      {
        optionalAuth: true,
      },
    );
  }

  async getAllCreators(options: { excludeDeleted?: boolean } = {}) {
    const query = new URLSearchParams();
    if (options.excludeDeleted) {
      query.set("excludeDeleted", "true");
    }
    const queryString = query.toString() ? `?${query.toString()}` : "";
    return this.apiRequest(`/creators${queryString}`, { optionalAuth: true });
  }

  async getLiveStreams(limit = 50, next?: string) {
    const params = new URLSearchParams();
    params.set('limit', limit.toString());
    if (next) {
      params.set('next', next);
    }
    return this.apiRequest(`/streams/live?${params.toString()}`, { optionalAuth: true });
  }

  async getLiveStream(streamId: string) {
    return this.apiRequest(`/streams/${streamId}`, { optionalAuth: true });
  }

  async getStreamSourceUrl(streamId: string) {
    const response = await this.apiRequest(`/streams/${streamId}/source`, { optionalAuth: true });
    // console.log(`[APIClient] Stream source URL:`, response);
    return response;    
  }

  async getStreamGifts(): Promise<StreamGiftsResponse> {
    return this.apiRequest('/streams/gifts', { optionalAuth: true });
  }

  async sendStreamMessage(streamId: string, payload: SendMessageRequest): Promise<SendMessageResponse> {
    return this.apiRequest(`/streams/${streamId}/chat`, {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  }

  async getStreamMentionableUsers(streamId: string): Promise<StreamMentionableUsersResponse> {
    return this.apiRequest(`/streams/${streamId}/mentionable-users`, { optionalAuth: true });
  }

  async checkStreamPayment(streamId: string) {    
    try {
      const result = await this.apiRequest(`/streams/${streamId}/pay`);
      if (!result.endTimestamp) {
        return 0;
      }
      return result.endTimestamp._seconds;
    } catch (error) {      
      // If it's a 401/403, return a default "no payment" response
      if (error instanceof Error && (error.message?.includes('401') || error.message?.includes('403'))) {
        console.log(`[APIClient] Authentication error, returning no payment response`);
        return 0;
      }
      
      throw error;
    }
  }

  async purchaseStreamTicket(streamId: string) {    
    const result = await this.apiRequest(`/streams/${streamId}/pay`, {
      method: 'POST',
      body: JSON.stringify({})
    });
    console.log(`[APIClient] ${streamId} - Purchase ticket result:`, {
      result,
      endTimestamp: result.endTimestamp,
      endTimestampSeconds: result.endTimestamp ? result.endTimestamp._seconds : null
    });
    return result.endTimestamp._seconds;
  }

  async getStreamLeaderboard(creatorId: string, params: {
    startDate?: number;
    endDate?: number;
    limit?: number;
    nextCursor?: string;
  } = {}) {
    const queryParams = new URLSearchParams();
    
    if (params.startDate) {
      queryParams.set('startDate', params.startDate.toString());
    }
    if (params.endDate) {
      queryParams.set('endDate', params.endDate.toString());
    }
    if (params.limit) {
      queryParams.set('limit', params.limit.toString());
    }
    if (params.nextCursor) {
      queryParams.set('nextCursor', params.nextCursor);
    }
    
    const endpoint = `/stream-leaderboard/${creatorId}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return this.apiRequest(endpoint, { optionalAuth: true });
  }

  async getServerTime() {
    try {
      const result = await this.apiRequest('/time', { optionalAuth: true });
      return result;
    } catch (error) {
      console.error('[APIClient] Failed to get server time:', error);
      throw error;
    }
  }

  async banStreamUser(creatorId: string, userId: string, reason: string, expiresAt?: number | null) {
    return this.apiRequest(`/banned-stream-user/${creatorId}`, {
      method: 'POST',
      body: JSON.stringify({
        userId,
        reason,
        expiresAt
      })
    });
  }

  async globalBanStreamUser(userId: string, reason: string, expiresAt?: number | null) {
    return this.apiRequest('/banned-stream-user', {
      method: 'POST',
      body: JSON.stringify({
        userId,
        reason,
        expiresAt
      })
    });
  }

  async postStreamChatPresence(streamId: string) {
    return this.apiRequest(`/streams/${streamId}/chat-presence`, {
      method: 'POST',
      body: JSON.stringify({})
    });
  }

  async getCampaignTopUsers(campaign: string) {
    return this.apiRequest(`/stream-leaderboard/top-users?campaign=${campaign}`, { optionalAuth: true });
  }

  async getCampaignTopCreators(campaign: string) {
    return this.apiRequest(`/stream-leaderboard/top-creators?campaign=${campaign}`, { optionalAuth: true });
  }

  // ======================
  // Creator Subscriptions
  // ======================
  async getCreatorSubscriptionPlan(creatorId: string) {
    return this.apiRequest(`/creators/${creatorId}/subscriptions/plan`, { optionalAuth: true });
  }

  async checkCreatorSubscription(creatorId: string) {
    return this.apiRequest(`/subscriptions/${creatorId}/check`, {optionalAuth: true});
  }

  async getSubscriptionPaymentToken(creatorId: string, redirectPath: string = '') {
    const encodedPath = encodeURIComponent(redirectPath);
    return this.apiRequest(`/creators/${creatorId}/subscriptions/get-token?redirect_path=${encodedPath}`, {
      method: 'POST',
      body: JSON.stringify({ creatorId }),
    });
  }

  async createSubscription(creatorId: string, payToken: string, tradeId: string, paymentType: string = 'CREDIT') {
    return this.apiRequest(`/creators/${creatorId}/subscriptions`, {
      method: 'POST',
      body: JSON.stringify({ payToken, tradeId, paymentType }),
    });
  }

  // Creator subscription management (requires creator auth)
  async getCreatorSubscriptionPlanDetails() {
    return this.creatorApiRequest(`/subscription-plan`);
  }

  async setSubscriptionPrice(price: number) {
    return this.creatorApiRequest(`/subscription-plan/price`, {
      method: 'POST',
      body: JSON.stringify({ price }),
    });
  }

  async listSubscribers(params?: { limit?: number; next?: string }) {
    const query = this.buildPaginationQuery(params);
    return this.creatorApiRequest(`/subscribers${query}`);
  }

  // User subscription management
  async listUserSubscriptions(params?: { limit?: number; next?: string }) {
    const query = this.buildPaginationQuery(params);
    return this.apiRequest(`/subscriptions${query}`);
  }

  async cancelSubscription(creatorId: string) {
    return this.apiRequest(`/subscriptions/cancel`, {
      method: 'POST',
      body: JSON.stringify({ creatorId }),
    });
  }

  // =====================
  // 1-on-1 Call APIs
  // =====================

  /**
   * Initiate a call to a creator
   */
  async initiateCall(creatorId: string, callType: 'voice' | 'video') {
    return this.apiRequest('/calls/initiate', {
      method: 'POST',
      body: JSON.stringify({ creatorId, callType }),
    });
  }

  /**
   * Get call stream URLs for the caller
   */
  async getCallUrls(callId: string) {
    return this.apiRequest(`/calls/${callId}/urls`);
  }

  /**
   * End a call
   */
  async endCall(callId: string) {
    return this.apiRequest(`/calls/${callId}/end`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  // Call payment is now handled automatically by request-auth on the server side
  // when Tencent calls the auth endpoint. No explicit client-side payment needed.

}

// 建立 Singleton 實例
const apiClient = new APIClient();

export default apiClient;
